import { useEffect, useRef, useState, useMemo } from 'react';
// react-markdown v10 ships React 19 types which are incompatible with React 18's JSX types.
import _ReactMarkdown from 'react-markdown';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ReactMarkdown = _ReactMarkdown as any;
import remarkGfm from 'remark-gfm';
import { CadeAvatar } from './CadeAvatar';
import { ReasoningTrace } from './ReasoningTrace';
import { useGraphEntities } from '../contexts/GraphEntitiesContext';
import { EntityChip } from './EntityChip';
import { EvidenceTd } from './markdownCells';

interface ToolCallEntry {
  toolName: string;
  toolUseId: string;
  status: 'running' | 'success' | 'error';
  summary?: string;
  inputPayload?: Record<string, unknown>;
  queryLanguage?: string;
  resultSummary?: string;
  latencyMs?: number;
}

interface TraceEntry {
  type: 'thinking' | 'tool';
  text?: string;
  toolUseId?: string;
}

interface Props {
  content: string;
  reasoningText?: string;
  toolCalls?: Map<string, ToolCallEntry>;
  traceEntries?: TraceEntry[];
}

// Mono timestamp for the Cade header — HH:MM:SS, matching the mockup.
function formatStreamTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

// Throttle markdown rendering to avoid re-parsing on every character.
// Raw text is shown instantly; markdown is re-rendered at most every 80ms.
export function StreamingMessage({ content, reasoningText, toolCalls, traceEntries }: Props) {
  const { graphNodeLabels } = useGraphEntities();
  const [rendered, setRendered] = useState(content);
  // Capture the start time once so the header timestamp stays stable across deltas.
  const [startTime] = useState(() => new Date());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRef = useRef(content);

  latestRef.current = content;

  useEffect(() => {
    if (timerRef.current === null) {
      setRendered(content);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        // Flush any content that arrived during the throttle window
        setRendered(latestRef.current);
      }, 80);
    }
  }, [content]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const markdownComponents = useMemo(() => ({
    a: ({ children, href, ...props }: React.ComponentPropsWithoutRef<'a'> & { href?: string }) => (
      <a href={href} {...props}>{children}</a>
    ),
    code: ({ children, className, ...props }: React.ComponentPropsWithoutRef<'code'> & { className?: string }) => {
      if (className) return <code className={className} {...props}>{children}</code>;
      const text = String(children);
      const textLower = text.toLowerCase();
      const ipWithoutPort = textLower.match(/^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}):\d{1,5}$/)?.[1] ?? null;

      let nodeId = graphNodeLabels.get(textLower);
      if (!nodeId && ipWithoutPort) nodeId = graphNodeLabels.get(ipWithoutPort);
      if (nodeId) {
        return <EntityChip label={text} nodeId={nodeId} />;
      }
      return <code {...props}>{children}</code>;
    },
    // Shared with MessageList (see markdownCells.tsx).
    td: EvidenceTd,
  }), [graphNodeLabels]);

  const hasReasoning = (reasoningText && reasoningText.length > 0) || (toolCalls && toolCalls.size > 0);

  return (
    <div className="message message-cade message-streaming">
      <div className="message-header">
        <CadeAvatar size={29} />
        <span className="message-sender message-sender-cade">Cade</span>
        <span className="message-time">{formatStreamTime(startTime)}</span>
      </div>
      <div className="message-content">
        {hasReasoning && (
          <ReasoningTrace
            reasoningText={reasoningText}
            toolCalls={toolCalls}
            traceEntries={traceEntries}
            isStreaming
          />
        )}
        {rendered ? (
          <>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{rendered}</ReactMarkdown>
            <span className="cursor-blink">▌</span>
          </>
        ) : (
          !hasReasoning && (
            <span className="message-thinking">
              Thinking…<span className="cursor-blink">▌</span>
            </span>
          )
        )}
      </div>
    </div>
  );
}
